import React, { useState, useEffect } from 'react';

import { useWindowSize } from 'hooks/useWindowSize';
import { fsDarkBlue } from 'global/colors';
import { Wrapper, Container } from '../../components';
import CurvedContainer from '../../components/CurvedContainer';
import { testimonialImages } from 'assets/testimonials/testimonials';
import { tileImages } from 'assets/tileImages/tileImages';

import {
  HeaderContainer,
  DoorBackgroundSpan,
  HeaderContentContainer,
  HeaderParagraphContainer,
  HeaderParagraph,
  FatText,
  HeaderVideoContainer,
  HeaderIframe,
  HeaderText,
  MarketingPointsContainer,
  MarketingPointContainer,
  MarketingPointImage,
  MarketingPointText,
  PitchAndEventVideosContainer,
  PitchAndEventVideosColumn,
  Paragraph,
  EventIframe,
  TestimonialsHeader,
  TestimonialsRow,
  TestimonialsColumn,
  TestimonialContainer,
  TextNoMargin,
  TestimonialsContainer,
  TestimonialText,
  TestimonialPersonContainer,
  TestimonialNameContainer,
  TestimonialAvatar,
} from './styles';

const testimonialsData = [
  {
    text: 'Vi valde att anordna en Code Challenge tillsammans med FutureSkill för att få möjlighet att träffa utvecklare på plats hos oss. Vi passade på att berätta om hur vi jobbar inom IT och de som ville fick självklart ställa frågor till oss. Code challengen blev väldigt lyckad och Futureskill var enkla att jobba med och proffs på att hålla i tävlingen. Vi kommer definitivt köra igen!',
    name: 'Anna Svedberg',
    company: 'Stena Line',
    image: testimonialImages.AnnaSvedbergStenaLine,
  },
  {
    text: 'Future Skills kodtävlingar har varit mycket uppskattade och väldigt professionellt utförda. Rekommenderas varmt!',
    name: 'Johan Skarin',
    company: 'AGREAT',
    image: testimonialImages.JohanSkarinAgreat,
  },
  {
    text: 'För oss var det självklart att arrangera en Code Challenges för att attrahera rätt kandidater till WirelessCar. Att då få experthjälp av Future skills, som har både kunskapen och erfarenheten av att sätta upp sådana koncept var toppen. Det var smidigt, proffsigt och lärorikt!',
    name: 'Martin Andersson',
    company: 'WirelessCar',
    image: testimonialImages.MartinAnderssonWirelessCar,
  },
  {
    text: 'Grymt och uppskattat koncept för både egen personal och blivande',
    name: 'Kimon Sfiris',
    company: 'Sigma Embedded Engineering',
    image: testimonialImages.kimonSfirisSigma,
  },
  {
    text: 'Det blev en succé då utvecklarna, som i vanliga fall inte arbetar tillsammans, fick träffas krossfunktionellt och utbyta erfarenheter. Ett gäng utvecklare som deltog är ovana vid par-programmering, efteråt blev den spontana reaktionen att det var oerhört lärorikt och det är något som de önskar göra mer! Och så klart blev det en spurt och hets i slutet på tävlingen, alla gav allt!',
    name: 'Alice Happ',
    company: 'Tre',
    image: testimonialImages.AliceHappTre,
  },
];

const getTestimonialsDataSlices = (columnCount) => {
  let res = [];
  let chunkSize = Math.ceil(testimonialsData.length / columnCount);
  for (let i = 0; i < testimonialsData.length; i += chunkSize) {
    res.push(testimonialsData.slice(i, i + chunkSize));
  }
  return res;
};

export default function CodeChallenge() {
  const [testimonials, setTestimonials] = useState(getTestimonialsDataSlices(1));
  const { width } = useWindowSize();

  useEffect(() => {
    if (width < 720) {
      if (testimonials.length !== 1) setTestimonials(getTestimonialsDataSlices(1));
    } else if (width < 1200) {
      if (testimonials.length !== 2) setTestimonials(getTestimonialsDataSlices(2));
    } else {
      if (testimonials.length !== 3) setTestimonials(getTestimonialsDataSlices(3));
    }
  }, [width, setTestimonials, testimonials.length]);

  const renderMarketingHeader = () => (
    <HeaderContainer>
      <DoorBackgroundSpan />
      <HeaderContentContainer>
        <HeaderText>Code Challenge</HeaderText>
        <HeaderVideoContainer>
          <HeaderIframe
            title="Future Skill Code Challenge Concept"
            src="https://player.vimeo.com/video/696551693?h=abacb4574b"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          ></HeaderIframe>
        </HeaderVideoContainer>
        <HeaderParagraphContainer>
          <HeaderParagraph>
            A Future Skill Code Challenge is an event <FatText>hosted on your office premises</FatText> where we visit
            you and bring a <FatText>code challenge</FatText> with us. For a duration of <FatText>2 hours</FatText> your
            colleagues will try to beat each other by writing as smart code as possible.
          </HeaderParagraph>
        </HeaderParagraphContainer>
      </HeaderContentContainer>
    </HeaderContainer>
  );

  const renderMarketingPoints = () => (
    <MarketingPointsContainer>
      <MarketingPointContainer>
        <MarketingPointImage src={tileImages.parentChildReadingTile} loading="lazy" alt="" />
        <MarketingPointText>Unique event that will encourage people to collaborate</MarketingPointText>
      </MarketingPointContainer>
      <MarketingPointContainer>
        <MarketingPointImage src={tileImages.flippboardTile} loading="lazy" alt="" />
        <MarketingPointText>Interactive - like solving a board game with code</MarketingPointText>
      </MarketingPointContainer>
      <MarketingPointContainer>
        <MarketingPointImage src={tileImages.flowerTile} loading="lazy" alt="" />
        <MarketingPointText>Low entry barrier in terms of code knowledge</MarketingPointText>
      </MarketingPointContainer>
      <MarketingPointContainer>
        <MarketingPointImage src={tileImages.talkingTile} loading="lazy" alt="" />
        <MarketingPointText>Suitable for both internal and external participants</MarketingPointText>
      </MarketingPointContainer>
      <MarketingPointContainer>
        <MarketingPointImage src={tileImages.resumeTile} loading="lazy" alt="" />
        <MarketingPointText>Minimal requirements for the employer - a venue, food & drinks</MarketingPointText>
      </MarketingPointContainer>
    </MarketingPointsContainer>
  );

  const renderVidoesAndExplainer = () => (
    <PitchAndEventVideosContainer>
      <PitchAndEventVideosColumn isColumnOne>
        <EventIframe
          title="FS Code Challenge With Kollmorgen"
          src="https://player.vimeo.com/video/709434215?h=501a914fba"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowfullscreen
        ></EventIframe>
        <Paragraph>
          The Future Skill Code Challenge is an excellent opportunity to improve your company’s{' '}
          <FatText>employer branding</FatText>. The code competitions are designed with the{' '}
          <FatText>participants in focus</FatText> and they all centre around an interactive canvas. Also, a Code
          Challenge event is a brilliant opportunity when it comes to <FatText>recruiting</FatText> new talents,
          allowing you to invite people who are not employed at your company.
        </Paragraph>
      </PitchAndEventVideosColumn>
      <PitchAndEventVideosColumn>
        <EventIframe
          title="FS Code Challenge With Agreat"
          src="https://player.vimeo.com/video/711555773?h=3785db0fdf"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowfullscreen
        ></EventIframe>
        <EventIframe
          title="FS Code Challenge With Monocl"
          src="https://player.vimeo.com/video/710682080?h=88d88454d4"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowfullscreen
        ></EventIframe>
      </PitchAndEventVideosColumn>
    </PitchAndEventVideosContainer>
  );

  const renderCustomerTestimonials = () => (
    <TestimonialsContainer>
      <TestimonialsHeader>Testimonials</TestimonialsHeader>
      <TestimonialsRow>
        {testimonials.map((testimonialValues, columnIndex) => (
          <TestimonialsColumn key={columnIndex}>
            {testimonialValues.map((testimonialValue, testimonialIndex) => (
              <TestimonialContainer key={testimonialIndex}>
                <TestimonialText>“{testimonialValue.text}”</TestimonialText>
                <TestimonialPersonContainer>
                  <TestimonialAvatar image={testimonialValue.image} />
                  <TestimonialNameContainer>
                    <TextNoMargin>
                      <b>{testimonialValue.name}</b>
                    </TextNoMargin>
                    <TextNoMargin>
                      <i>{testimonialValue.company}</i>
                    </TextNoMargin>
                  </TestimonialNameContainer>
                </TestimonialPersonContainer>
              </TestimonialContainer>
            ))}
          </TestimonialsColumn>
        ))}
      </TestimonialsRow>
    </TestimonialsContainer>
  );

  return (
    <Wrapper color="transparent">
      <CurvedContainer bottom height={110} color={fsDarkBlue} />
      <Container column center>
        {renderMarketingHeader()}
        {renderMarketingPoints()}
        {renderVidoesAndExplainer()}
        {renderCustomerTestimonials()}
      </Container>
    </Wrapper>
  );
}
